/**
 * ArtifactDetail Page
 *
 * Displays a single artifact with its metadata and associated files.
 * Allows downloading files attached to the artifact.
 */

import { A, useParams, useNavigate } from '@solidjs/router';
import {
  type Component,
  createResource,
  createSignal,
  For,
  Show,
} from 'solid-js';
import {
  fetchArtifact,
  downloadFile,
  deleteArtifact as deleteArtifactApi,
  type ArtifactWithFiles,
  type FileInfo,
} from '../services/api';

const ArtifactDetail: Component = () => {
  const params = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [isDeleting, setIsDeleting] = createSignal(false);
  const [downloadingId, setDownloadingId] = createSignal<string | null>(null);

  const [artifact, { refetch }] = createResource(
    () => params.id,
    async (id) => {
      return fetchArtifact(id);
    }
  );

  const handleDownload = async (file: FileInfo) => {
    setDownloadingId(file.id);
    try {
      await downloadFile(file.id);
    } catch (error) {
      console.error('Download failed:', error);
    } finally {
      setDownloadingId(null);
    }
  };

  const handleDelete = async () => {
    if (!confirm('Are you sure you want to delete this artifact?')) return;

    setIsDeleting(true);
    try {
      await deleteArtifactApi(params.id);
      navigate('/artifacts');
    } catch (error) {
      console.error('Delete failed:', error);
      setIsDeleting(false);
    }
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return `${date.toLocaleDateString()} ${date.toLocaleTimeString([], {
      hour: '2-digit',
      minute: '2-digit',
    })}`;
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  };

  const getFileIcon = (mimeType: string): string => {
    if (mimeType.startsWith('image/')) return 'M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z';
    if (mimeType.includes('pdf')) return 'M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z';
    if (mimeType.startsWith('text/') || mimeType.includes('json') || mimeType.includes('javascript') || mimeType.includes('typescript')) return 'M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4';
    if (mimeType.includes('csv') || mimeType.includes('spreadsheet')) return 'M3 10h18M3 14h18m-9-4v8m-7 0h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z';
    return 'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z';
  };

  const getFileTypeLabel = (mimeType: string): string => {
    if (mimeType.startsWith('image/')) return 'Image';
    if (mimeType.includes('pdf')) return 'PDF';
    if (mimeType.includes('json')) return 'JSON';
    if (mimeType.includes('javascript') || mimeType.includes('typescript')) return 'Code';
    if (mimeType.includes('markdown')) return 'Markdown';
    if (mimeType.includes('csv')) return 'CSV';
    if (mimeType.includes('yaml')) return 'YAML';
    if (mimeType.startsWith('text/')) return 'Text';
    return 'File';
  };

  const getTypeColor = (type: string): string => {
    switch (type) {
      case 'expression': return 'purple';
      case 'report': return 'blue';
      case 'document': return 'green';
      case 'dataset': return 'yellow';
      case 'software': return 'red';
      case 'file': return 'gray';
      default: return 'gray';
    }
  };

  const getVisibilityIcon = (visibility: string) => {
    switch (visibility) {
      case 'public':
        return 'M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z';
      case 'preview':
        return 'M15 12a3 3 0 11-6 0 3 3 0 016 0z M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z';
      default:
        return 'M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z';
    }
  };

  return (
    <div class="space-y-6">
      {/* Header */}
      <div class="flex items-center gap-4">
        <A
          href="/artifacts"
          class="p-2 rounded-lg text-gray-400 hover:text-gray-200 hover:bg-gray-800 transition-colors"
        >
          <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
          </svg>
        </A>
        <h1 class="text-2xl font-semibold text-gray-100">Artifact Details</h1>
      </div>

      {/* Loading State */}
      <Show when={artifact.loading}>
        <div class="flex items-center justify-center py-16">
          <div class="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-purple-500" />
        </div>
      </Show>

      {/* Error State */}
      <Show when={artifact.error}>
        <div class="bg-red-900/20 border border-red-800 rounded-lg p-6 text-center">
          <svg class="w-12 h-12 mx-auto text-red-500 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
          <h3 class="text-lg font-medium text-red-400 mb-2">Failed to load artifact</h3>
          <p class="text-gray-400 mb-4">{(artifact.error as Error)?.message || 'Unknown error'}</p>
          <button
            onClick={() => refetch()}
            class="px-4 py-2 bg-red-600 hover:bg-red-500 text-white rounded-lg transition-colors"
          >
            Try Again
          </button>
        </div>
      </Show>

      {/* Artifact Content */}
      <Show when={artifact()}>
        {(data) => (
          <div class="space-y-6">
            {/* Artifact Header Card */}
            <div class="bg-gray-900/50 border border-gray-800 rounded-lg p-6">
              <div class="flex items-start justify-between mb-4">
                <div class="flex items-center gap-3">
                  <span class={`px-3 py-1 rounded-lg text-sm font-medium bg-${getTypeColor(data().type)}-500/20 text-${getTypeColor(data().type)}-400 capitalize`}>
                    {data().type}
                  </span>
                  <Show when={data().version_num && data().version_num! > 1}>
                    <span class="px-2 py-1 rounded text-xs bg-gray-700 text-gray-400">
                      v{data().version_num}
                    </span>
                  </Show>
                  <span class="flex items-center gap-1 text-xs text-gray-500">
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d={getVisibilityIcon(data().visibility)} />
                    </svg>
                    {data().visibility}
                  </span>
                </div>

                {/* Actions */}
                <Show when={data().isOwner}>
                  <button
                    onClick={handleDelete}
                    disabled={isDeleting()}
                    class="px-3 py-1.5 rounded-lg text-sm text-red-400 hover:text-red-300 hover:bg-red-900/20 disabled:opacity-50 transition-colors"
                  >
                    {isDeleting() ? 'Deleting...' : 'Delete'}
                  </button>
                </Show>
              </div>

              <h2 class="text-xl font-semibold text-gray-100 mb-2">{data().title}</h2>

              <Show when={data().description}>
                <p class="text-gray-400 mb-4">{data().description}</p>
              </Show>

              <div class="flex flex-wrap items-center gap-4 text-sm text-gray-500">
                <Show when={data().projectId}>
                  <A
                    href={`/projects/${data().projectId}`}
                    class="flex items-center gap-1 hover:text-purple-400 transition-colors"
                  >
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
                    </svg>
                    View Project
                  </A>
                </Show>
                <span class="flex items-center gap-1">
                  <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                  {data().viewCount} views
                </span>
                <span>Created: {formatDate(data().createdAt)}</span>
                <span>Updated: {formatDate(data().updatedAt)}</span>
              </div>

              {/* Tags */}
              <Show when={data().tags && data().tags!.length > 0}>
                <div class="flex flex-wrap gap-2 mt-4">
                  <For each={data().tags}>
                    {(tag) => (
                      <span class="px-2 py-1 rounded-full text-xs bg-gray-800 text-gray-400">
                        {tag}
                      </span>
                    )}
                  </For>
                </div>
              </Show>
            </div>

            {/* Files Section */}
            <div class="bg-gray-900/50 border border-gray-800 rounded-lg p-6">
              <div class="flex items-center justify-between mb-4">
                <h3 class="text-lg font-medium text-gray-200">Files</h3>
                <span class="px-2.5 py-1 rounded-lg bg-gray-800 text-sm text-gray-400">
                  {data().files.length} file{data().files.length !== 1 ? 's' : ''}
                </span>
              </div>

              {/* Empty State */}
              <Show when={data().files.length === 0}>
                <div class="text-center py-8 text-gray-500">
                  <svg class="w-12 h-12 mx-auto mb-3 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
                  </svg>
                  <p>No files attached to this artifact</p>
                </div>
              </Show>

              {/* File List */}
              <Show when={data().files.length > 0}>
                <div class="divide-y divide-gray-800 rounded-lg border border-gray-800 overflow-hidden">
                  <For each={data().files}>
                    {(file) => (
                      <div class="flex items-center gap-4 p-4 bg-gray-900/50 hover:bg-gray-800/50 transition-colors">
                        {/* File Icon */}
                        <div class="w-10 h-10 rounded-lg bg-gray-800 flex items-center justify-center flex-shrink-0">
                          <svg class="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d={getFileIcon(file.mimeType)} />
                          </svg>
                        </div>

                        {/* File Info */}
                        <div class="flex-1 min-w-0">
                          <p class="text-sm font-medium text-gray-200 truncate">
                            {file.displayName || file.name}
                          </p>
                          <div class="flex items-center gap-3 text-xs text-gray-500">
                            <span class="px-1.5 py-0.5 bg-gray-800 rounded text-gray-400">
                              {getFileTypeLabel(file.mimeType)}
                            </span>
                            <span>{formatFileSize(file.sizeBytes)}</span>
                            <span>{formatDate(file.createdAt)}</span>
                          </div>
                          <Show when={file.description}>
                            <p class="text-xs text-gray-500 mt-1 truncate">{file.description}</p>
                          </Show>
                        </div>

                        {/* Download Button */}
                        <button
                          onClick={() => handleDownload(file)}
                          disabled={downloadingId() === file.id}
                          class="flex items-center gap-2 px-4 py-2 rounded-lg bg-purple-600 hover:bg-purple-500 text-white text-sm font-medium disabled:opacity-50 transition-colors"
                          title="Download"
                        >
                          <Show
                            when={downloadingId() !== file.id}
                            fallback={
                              <svg class="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
                                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                              </svg>
                            }
                          >
                            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                            </svg>
                          </Show>
                          Download
                        </button>
                      </div>
                    )}
                  </For>
                </div>
              </Show>
            </div>

            {/* Content Preview (for expressions) */}
            <Show when={data().type === 'expression' && data().content}>
              <div class="bg-gray-900/50 border border-gray-800 rounded-lg p-6">
                <h3 class="text-lg font-medium text-gray-200 mb-4">Content Preview</h3>
                <div class="bg-gray-950 rounded-lg p-4 overflow-auto max-h-96">
                  <pre class="text-sm text-gray-300 whitespace-pre-wrap">
                    {JSON.stringify(data().content, null, 2)}
                  </pre>
                </div>
              </div>
            </Show>
          </div>
        )}
      </Show>
    </div>
  );
};

export default ArtifactDetail;
